import { z } from "zod";

export const KatanaConnectionStatus = {
  ONLINE: "online",
} as const;
export type KatanaConnectionStatus = (typeof KatanaConnectionStatus)[keyof typeof KatanaConnectionStatus];

/**
 * Body for `POST /v1/exchange-account/katana/delegation-prepare` (phase 1).
 *
 * Three fields the user pastes from perps.katana.network: API key UUID, HMAC
 * secret (shown once), and the custody EVM address already associated with
 * the API key. No private key — the BE mints a fresh delegated keypair
 * server-side; the user authorizes it by signing the returned EIP-712
 * envelope with their connected wallet in phase 2.
 */
export const KatanaDelegationPrepareBodySchema = z.object({
  apiKey: z.string().uuid({ message: "apiKey must be the UUID issued by Katana" }),
  apiSecret: z.string().min(16, "apiSecret must match the secret displayed once by Katana"),
  walletAddress: z
    .string()
    .regex(/^0x[a-fA-F0-9]{40}$/, "walletAddress must be a 0x-prefixed 20-byte hex")
    .transform((v) => v.toLowerCase()),
});
export type KatanaDelegationPrepareBodyDto = z.infer<typeof KatanaDelegationPrepareBodySchema>;

/**
 * Response of `POST /v1/exchange-account/katana/delegation-prepare`.
 *
 * `eip712TypedData` is shaped to feed wagmi's `signTypedDataAsync` verbatim —
 * matches the GRVT builder-prepare envelope already consumed by hub-v2's
 * `useSignTypedDataWithConnection`. The FE call collapses to:
 *
 *   const signature = await signTypedDataAsync(response.eip712TypedData);
 *
 * Production domain: `{ name: "KatanaPerps", version: "2.0.0", chainId: 747474,
 * verifyingContract: "0x6223…633e" }`. Sandbox swaps `chainId` + contract
 * (sandbox chainId is currently unresolved — the BE throws if anyone tries).
 *
 * `message.nonce` is a `uint128` decimal string (UUID v1 reinterpreted as a
 * big-endian 128-bit integer). viem/ethers accept stringified bigints for
 * `uint*` typed-data fields — pass through verbatim. `message.message` is the
 * fixed literal Katana's server expects byte-for-byte.
 */
export const KatanaDelegationPrepareResultSchema = z.object({
  prepareId: z.string().uuid(),
  eip712TypedData: z.object({
    types: z.record(z.string(), z.array(z.object({ name: z.string(), type: z.string() }))),
    primaryType: z.literal("DelegatedKeyAuthorization"),
    domain: z.object({
      name: z.literal("KatanaPerps"),
      version: z.string(),
      chainId: z.number().int().positive(),
      verifyingContract: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
    }),
    message: z.object({
      nonce: z.string().regex(/^\d+$/, "nonce must be a uint128 decimal string"),
      delegatedPublicKey: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
      message: z.literal("Sign this free message to prove you control this wallet"),
    }),
  }),
});
export type KatanaDelegationPrepareResultDto = z.infer<typeof KatanaDelegationPrepareResultSchema>;

/**
 * Body for `POST /v1/exchange-account/katana/delegation-authorize` (phase 2).
 *
 * Flat 0x-hex signature, NOT split (r,s,v). wagmi returns flat, viem's
 * `recoverTypedDataAddress` takes flat, Katana's `/v1/delegatedKeys` wire body
 * takes flat — splitting and rejoining adds round-trip risk for zero gain.
 */
export const KatanaDelegationAuthorizeBodySchema = z.object({
  prepareId: z.string().uuid(),
  signature: z
    .string()
    .regex(/^0x[a-fA-F0-9]{8,264}$/, { message: "Invalid EVM signature format." }) as z.ZodType<`0x${string}`>,
});
export type KatanaDelegationAuthorizeBodyDto = z.infer<typeof KatanaDelegationAuthorizeBodySchema>;

/**
 * Response of `POST /v1/exchange-account/katana/delegation-authorize`. Mirrors
 * GRVT's `AddGrvtAccountSchema` so the FE response handlers can share shape.
 */
export const AddKatanaAccountSchema = z.object({
  accountId: z.number().int().positive(),
  walletAddress: z.string().min(1),
  status: z.string(),
});
export type AddKatanaAccountDto = z.infer<typeof AddKatanaAccountSchema>;

/**
 * Ephemeral state captured during Katana prepare phase and replayed at
 * authorize phase. Persisted in Redis under a server-assigned `prepareId`
 * (30-minute TTL). The user's connected wallet signs the EIP-712 envelope
 * derived from these fields; we never see the custody private key.
 */
export type KatanaPrepareData = {
  hubUserId: number;
  apiKey: string;
  apiSecret: string;
  walletAddress: `0x${string}`;
  delegatedPublicKey: `0x${string}`;
  delegatedPrivateKey: `0x${string}`;
  /** UUID v1 — reused as `nonce` in both the EIP-712 message and the `/v1/delegatedKeys` body. */
  nonce: string;
  /** Stringified `uint128` (big-endian UUID bytes). The form signed by the wallet. */
  nonceUint128: string;
  /** Stable label the user sees in Katana's "Delegated keys" UI. */
  delegatedKeyName: string;
};
